<template>
  <div class="line-charts-container">
    <div id="readRateChart" class="chart"></div>
  </div>
</template>

<script>
import Highcharts from "highcharts";
import moment from "moment";

export default {
  name: "LineChart",
  props: {
    gmailInboxData: {
      type: Array,
      required: true,
    },
  },
  watch: {
    gmailInboxData: {
      handler(newData) {
        this.updateChart(newData);
      },
      deep: true,
    },
  },
  mounted() {
    this.initChart();
    window.addEventListener("resize", this.reflowChart);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.reflowChart);
  },
  methods: {
    initChart() {
      this.chart = Highcharts.chart("readRateChart", {
        lang: {
          decimalPoint: ".",
          thousandsSep: ",",
        },
        chart: {
          type: "line",
          height: this.getChartHeight(),
        },
        title: {
          text: "Gmail Inbox Engagement",
          style: {
            fontSize: "16px",
          },
        },
        xAxis: {
          categories: [],
          labels: {
            rotation: -45,
          },
        },
        yAxis: {
          title: {
            text: "Inbox Count",
          },
          labels: {
            formatter: function () {
              return this.value;
            },
          },
          tickInterval: 25,
          max: 100,
          min: 0,
        },
        series: [
          {
            name: "Gmail Inbox",
            data: [],
            lineWidth: 3,
            color: "#38761d",
            dashStyle: "ShortDash",
          },
        ],
        credits: {
          enabled: false,
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 600,
              },
              chartOptions: {
                chart: {
                  height: 180,
                },
                yAxis: {
                    tickInterval: 25,
                    max: 100,
                    min: 0,
                },
                xAxis: {
                  labels: { enabled: false },
                },
                title: {
                  style: { fontSize: "14px" },
                },
              },
            },
            {
              condition: {
                minWidth: 768,
              },
              chartOptions: {
                chart: {
                  height: 250,
                },
                xAxis: {
                  labels: { enabled: true },
                },
                title: {
                  style: { fontSize: "18px" },
                },
              },
            },
          ],
        },
      });
    },

    updateChart(data) {
      if (this.chart && Array.isArray(data)) {
        const aggregatedData = data.reduce((acc, item) => {
          const fullDate = moment(item.date).format("YYYY-MM-DD");
          const displayDate = moment(item.date).format("DD-MMM");

          if (!acc[fullDate]) {
            acc[fullDate] = { fullDate, displayDate, totalEmails: 0 };
          }
          acc[fullDate].totalEmails += item.value ?? 0;

          return acc;
        }, {});

        const sortedData = Object.values(aggregatedData).sort(
          (a, b) => moment(a.fullDate).valueOf() - moment(b.fullDate).valueOf()
        );

        const categories = sortedData.map((item) => item.displayDate);
        const values = sortedData.map((item) => item.totalEmails);

        this.chart.xAxis[0].setCategories(categories);
        this.chart.series[0].setData(values);
      }
    },

    reflowChart() {
      if (this.chart) {
        this.chart.setSize(null, this.getChartHeight(), false);
      }
    },

    getChartHeight() {
      const screenWidth = window.innerWidth;
      if (screenWidth < 600) return 180;
      if (screenWidth < 768) return 220;
      return 280;
    },
  },
};
</script>

<style scoped>
.line-charts-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.chart {
  width: 100%;
  min-width: 300px;
  height: 200px;
}

/* Responsive Chart Scaling */
@media (min-width: 768px) {
  .chart {
    height: 200px;
  }
}

@media (min-width: 1024px) {
  .chart {
    height: 280px;
  }
}
</style>
